import {Component, EventEmitter, inject, Input, NgZone, Output, ViewChild} from '@angular/core';
import {isArray} from 'lodash';
import {MenuItem, TreeNode} from 'primeng/api';
import {firstValueFrom, SubscriptionLike} from 'rxjs';
import {
    ECpmCTaskAttributes1ApiEnum,
    ECpmCTaskStatusApiEnum,
    ECpmCTaskTypeApiEnum
} from '../../../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-task.api-enums';
import {CpmCTaskApiModel} from '../../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-task.api-model';
import {
    CpmCTaskDeleteApiSchema,
    CpmCTaskReorderApiSchema,
    CpmCTaskUpdateApiSchema
} from '../../../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-task.api-schemas';
import {CpmCTaskApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-task.api-service';
import {ETreeColDataType, ETreeColFilterType} from '../../../../../sdk/src/components/generic-tree/tree-col-def';
import {IBaseCRUDComponent} from '../../../../../sdk/src/components/ibase-crud/ibase-crud.component';
import {EDeleteMode} from '../../../../../sdk/src/enums/delete-mode.enum';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {SdkImportsModule} from '../../../../../sdk/src/sdk-imports';
import {TreeUtils} from '../../../../../sdk/src/utils/tree.utils';
import {
    NewEditTaskModalComponent,
    NewEditTaskModalConfig,
    NewEditTaskModalResult
} from '../../../modals/new-edit-task-modal/new-edit-task-modal.component';
import {
    TaskDependsWarningModalComponent,
    TaskDependsWarningModalConfig
} from '../../../modals/task/task-depends-confirm-warning-modal/task-depends-confirm-warning-modal';
import {SdkBridge} from '../../../sdk-bridge';
import {TaskUtils} from '../../../utils/task.utils';

import {TaskTreeViewExtension} from '../../../api-extensions/views/task-tree.view-extension';
import {TaskExecutionsModalComponent} from '../../../modals/task/task-executions-modal/task-executions-modal.component';

import {CpmCServiceRequestUpdateV1ApiEvent} from '../../../../../gen/cpm-c-api-ts/src/events/c-customer/cpmc-service-request.api-events';
import {CpmCTaskExecutionApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-task-execution.api-service';
import {EDialogOpenMode} from '../../../../../sdk/src/enums/dialog-open-mode.enum';
import {
    JsonSchemaFormModalComponent,
    JsonSchemaFormModalResult
} from '../../../../../sdk/src/modals/json-schema-form-modal/json-schema-form-modal.component';
import {Oo8EventServiceChannelDataMessage} from '../../../../../sdk/src/services/oo8-event.service';
import {CoalescedRunner, KeyedUpdateBuffer} from '../../../../../sdk/src/utils/coalescing.utils';
import {DateUtils} from '../../../../../sdk/src/utils/date.utils';
import {JsonSchemaUtils} from '../../../../../sdk/src/utils/json-schema.utils';
import {TaskStatusDataCellDisplayComponent} from '../../../data-cell-display/task-status-data-cell-display/task-status-data-cell-display.component';
import {ActionJobsUtils} from '../../../utils/action-jobs.utils';
import {
    GanttDateChangeEvent,
    GanttDateEditRequestEvent,
    TaskGanttPanelComponent
} from './task-gantt-panel/task-gantt-panel.component';

interface TaskStatusUpdate {
    status: ECpmCTaskStatusApiEnum;
    attributes1?: number | null;
}

/**
 * Task CRUD table component
 */
@Component({
    selector: 'app-task-crud',
    templateUrl: './task-crud.component.html',
    imports: [PrimeNgImportsModule, SdkImportsModule, TaskGanttPanelComponent]
})
export class TaskCRUDComponent extends IBaseCRUDComponent<
    TaskTreeViewExtension,
    NewEditTaskModalComponent,
    NewEditTaskModalConfig,
    NewEditTaskModalResult,
    CpmCTaskDeleteApiSchema
> {
    protected readonly CpmCTaskApiService = CpmCTaskApiService;

    /** Coalescing window for refreshes requested by Oo8 update events */
    private static readonly EVENT_REFRESH_DEBOUNCE_MS = 300;
    /** Maximum time a refresh request can be delayed by a continuous stream of events */
    private static readonly EVENT_REFRESH_MAX_WAIT_MS = 2000;
    /** Coalescing window for in place status updates, also used as max wait so that a continuous
     * stream of status events is applied at most once per window */
    private static readonly STATUS_UPDATE_DEBOUNCE_MS = 300;

    // ===============================================================
    // ===============================================================

    protected _helpVisible = false;
    protected _selection: unknown;
    protected _updateEventSubscription: SubscriptionLike | undefined;

    private readonly _ngZone: NgZone = inject(NgZone);

    /** Task UUID to tree node index, rebuilt on every refresh to avoid walking the tree on each event */
    private _taskNodeIndex = new Map<string, TreeNode<TaskTreeViewExtension>>();

    /** Reloads requested by Oo8 events, coalesced so that a burst triggers a single reload */
    private readonly _eventRefreshRunner = new CoalescedRunner(() => this.asyncCritical(() => this.onRefresh()), {
        debounceMS: TaskCRUDComponent.EVENT_REFRESH_DEBOUNCE_MS,
        maxWaitMS: TaskCRUDComponent.EVENT_REFRESH_MAX_WAIT_MS,
        zone: this._ngZone
    });
    /** In place status updates, batched so that a chain is applied at most once per window */

    private readonly _statusUpdateBuffer = new KeyedUpdateBuffer<string, TaskStatusUpdate>(
        (updates) => this.applyStatusUpdates(updates),
        {
            debounceMS: TaskCRUDComponent.STATUS_UPDATE_DEBOUNCE_MS,
            maxWaitMS: TaskCRUDComponent.STATUS_UPDATE_DEBOUNCE_MS,
            zone: this._ngZone
        }
    );

    // ===============================================================
    // Gantt view
    // ===============================================================

    @Input()
    public showNewButton = true;

    @Input()
    public selectedTask: TreeNode<TaskTreeViewExtension> | null = null;
    @Output()
    public selectedTaskChange = new EventEmitter<TreeNode<TaskTreeViewExtension> | null>();

    @Input()
    public taskList: TaskTreeViewExtension[] | null = null;
    @Output()
    public taskListChange = new EventEmitter<TaskTreeViewExtension[] | null>();

    @Output()
    public $onSelectedReloaded = new EventEmitter<void>();

    @ViewChild(TaskGanttPanelComponent)
    public gantt?: TaskGanttPanelComponent;
    public ganttVisible = false;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure({
            // Model config
            modelName: 'Task',
            getModelHumanIdentifier: (model: TaskTreeViewExtension) => model.name ?? '<Unknown task>',

            // Service config
            deleteMode: EDeleteMode.CONFIRM_REENFORCED,
            deleteModelService: (modelToDelete: TaskTreeViewExtension, payload?: CpmCTaskDeleteApiSchema) =>
                CpmCTaskApiService.deleteTask(modelToDelete.uuid, payload),

            // Modal config
            modalType: NewEditTaskModalComponent,
            getDialogServiceParameters: NewEditTaskModalComponent.getDialogServiceParameters,
            modalExtendedConfigBuilder: (model?: TaskTreeViewExtension) => new NewEditTaskModalConfig(model),

            // Authorization config
            authorizationNew: CpmCTaskApiService.endpointNew(this.FAKE_UUID, this.FAKE_UUID),
            authorizationEdit: CpmCTaskApiService.endpointEdit(this.FAKE_UUID, this.FAKE_UUID, this.FAKE_UUID),
            authorizationDelete: CpmCTaskApiService.endpointDelete(this.FAKE_UUID, this.FAKE_UUID, this.FAKE_UUID)
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        const channelName = `customer.${this.globalsService.sessionService.getCustomer()?.uuid}.service-request.${this.globalsService.sessionService.getServiceRequest()?.uuid}.update_v1`;
        this._updateEventSubscription = this.globalsService.oo8EventService.subscribe(
            channelName,
            this.onOo8Event_UpdateV1.bind(this)
        );
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    protected override async onDestroy(): Promise<void> {
        this._eventRefreshRunner.dispose();
        this._statusUpdateBuffer.dispose();

        if (this._updateEventSubscription) {
            this._updateEventSubscription.unsubscribe();
            this._updateEventSubscription = undefined;
        }
    }

    /**
     * Rebuild the task UUID to tree node index
     */
    private rebuildTaskNodeIndex(): void {
        this._taskNodeIndex = TreeUtils.indexTreeNodesByKey(this.treeItems);
    }

    // ===============================================================
    // ===============================================================

    /**
     * On refresh (Error handling is done in the parent)
     */
    public override async onRefresh(): Promise<void> {
        const expandedTaskSnapshot: string[] = [];
        TreeUtils.treeNodesSnapshotExpanded(this.treeItems, expandedTaskSnapshot);

        const result = await firstValueFrom(CpmCTaskApiService.getTaskTree({}, CpmCTaskApiService.dPaginatorConfig));
        this.items = result.data;
        this.totalItemCount = result.total;

        this.taskListChange.next(this.items);

        this.treeItems = TaskUtils.buildTaskTree(this.items, {
            fnDecorate: (x) => {
                x.data!._computed_deps_col = `${x.data!.dependency_count} | ${x.data!.dependant_count}`;
                x.data!._computed_deps_col_tooltip = `${x.data!.dependency_count} task(s) dependency\n${x.data!.dependant_count} dependant task(s)`;

                if (x.data!.task_type == ECpmCTaskTypeApiEnum.GROUP) {
                    x.icon = 'pi pi-folder';

                    if ((x.data!.attributes1 & ECpmCTaskAttributes1ApiEnum.GROUP_AUTO_EXPAND) != 0) {
                        expandedTaskSnapshot.push(x.key!);
                        x.icon = 'pi pi-folder-open';
                    }

                    x.style = {fontWeight: 'bold'};
                } else if (TaskUtils.isMilestone(x.data)) {
                    const isReady = x.data!.status === ECpmCTaskStatusApiEnum.READY;
                    x.icon = isReady ? 'pi pi-flag' : 'pi pi-flag-fill';

                    if (!isReady) {
                        const statusTag = TaskUtils.statusTagsByStatus[x.data!.status];
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (x as any).iconStyle = {
                            color: statusTag.iconStyle!['color']
                        };
                    }
                } else if (x.data?.task_type === ECpmCTaskTypeApiEnum.SMART) {
                    x.icon = ActionJobsUtils.ICON_PLUGIN_FUNCTION;

                    if ((x.data?.attributes1 & ECpmCTaskAttributes1ApiEnum.SMART_REQUIRES_MANUAL_INPUT) != 0)
                        x.icon = TaskUtils.ICON_REQUIRE_MANUAL_INPUT;
                }
            }
        });
        TaskUtils.sortTaskTree(this.treeItems);
        TaskUtils.addNumbersToTaskTree(this.treeItems, 'name');
        TreeUtils.treeNodesRestoreExpanded(this.treeItems, expandedTaskSnapshot, false);

        this.rebuildTaskNodeIndex();

        if (this.selectedTask) {
            this.selectedTask = TreeUtils.findTreeNodeByKey(this.treeItems, this.selectedTask.key!) ?? null;
            this.selectedTaskChange.next(this.selectedTask);

            this.$onSelectedReloaded.next();
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * Oo8Event: customer.UUID.service-request.UUID.update_v1
     * @param data Base message
     * @param payload Decoded payload
     */
    private onOo8Event_UpdateV1(data: Oo8EventServiceChannelDataMessage, payload: CpmCServiceRequestUpdateV1ApiEvent) {
        if (payload.created_task_uuid || payload.deleted_task_uuid) this._eventRefreshRunner.request();
        else if (payload.updated_task_uuid) {
            if (payload.updated_task_status)
                this._statusUpdateBuffer.set(payload.updated_task_uuid, {
                    status: payload.updated_task_status,
                    attributes1: payload.updated_task_attributes1
                });
            else this._eventRefreshRunner.request();
        }
    }

    /**
     * Apply a batch of in place status updates
     * @param updates Latest status per task UUID
     */
    private applyStatusUpdates(updates: Map<string, TaskStatusUpdate>): void {
        let unknownTask = false;

        updates.forEach((update, taskUUID) => {
            const taskTreeNode = this._taskNodeIndex.get(taskUUID);
            if (taskTreeNode?.data) {
                taskTreeNode.data.status = update.status;
                if (update.attributes1 != null) taskTreeNode.data.attributes1 = update.attributes1;
            } else unknownTask = true;
        });

        // A task we do not know about yet means the tree is out of date
        if (unknownTask) this._eventRefreshRunner.request();
    }

    // ===============================================================
    // ===============================================================

    /**
     * On new group button pressed
     */
    public async onNewGroup(): Promise<void> {
        if (!this.modalType || !this.getDialogServiceParameters || !this.modalExtendedConfigBuilder) return;

        const config = this.modalExtendedConfigBuilder?.();
        if (!config) return;
        config.initialTaskType = ECpmCTaskTypeApiEnum.GROUP;

        this.handleDialogRef(
            this.globalsService.dialogService.open(
                this.modalType,
                NewEditTaskModalComponent.getDialogServiceParameters({
                    dialogOpenMode: EDialogOpenMode.NEW,
                    extended: config
                })
            )
        );
    }
    /**
     * On new task button pressed
     */
    public async onNewTask(): Promise<void> {
        if (!this.modalType || !this.getDialogServiceParameters || !this.modalExtendedConfigBuilder) return;

        const config = this.modalExtendedConfigBuilder?.();
        if (!config) return;

        config.initialTaskType = ECpmCTaskTypeApiEnum.SIMPLE;
        this.handleDialogRef(
            this.globalsService.dialogService.open(
                this.modalType,
                NewEditTaskModalComponent.getDialogServiceParameters({
                    dialogOpenMode: EDialogOpenMode.NEW,
                    extended: config
                })
            )
        );
    }

    // ===============================================================
    // Gantt controls
    // ===============================================================

    /**
     * Toggle Gantt view
     */
    public toggleGantt(): void {
        this.ganttVisible = !this.ganttVisible;
    }
    /**
     * Handle date change from Gantt drag & drop
     */
    public onGanttDateChange(event: GanttDateChangeEvent): void {
        this.asyncCritical(async () => {
            await firstValueFrom(CpmCTaskApiService.updateTask(event.task.uuid, event.changes));
            await this.onRefresh();
        });
    }

    /**
     * Handle click on a date cell in the Gantt — opens date picker modal
     */
    public onGanttDateEditRequest(event: GanttDateEditRequestEvent): void {
        const fieldLabels: Record<GanttDateEditRequestEvent['field'], string> = {
            start_date: 'Task start date',
            end_date: 'Task end date',
            due_date: 'Task due date'
        };
        const currentValue = event.task[event.field];
        this.globalsService.dialogService
            .open(
                JsonSchemaFormModalComponent,
                JsonSchemaFormModalComponent.getDialogServiceParameters({
                    title: fieldLabels[event.field],
                    schema: JsonSchemaUtils.autoDatePropety(true),
                    data: currentValue ? new Date(currentValue) : null
                })
            )
            .onClose.subscribe(async (res: JsonSchemaFormModalResult) => {
                if (!res.validated) return;
                const newDate = res.data ? new Date(res.data as string) : null;

                if (newDate) {
                    const startDate = event.task.start_date ? new Date(event.task.start_date) : null;
                    const endDate = event.task.end_date ? new Date(event.task.end_date) : null;

                    if (event.field === 'end_date' && startDate && newDate < startDate) {
                        this.globalsService.messageService.add({
                            key: this.overrideMessagesKey,
                            severity: 'warn',
                            detail: 'End date cannot be before start date.'
                        });
                        return;
                    }
                    if (event.field === 'start_date' && endDate && newDate > endDate) {
                        this.globalsService.messageService.add({
                            key: this.overrideMessagesKey,
                            severity: 'warn',
                            detail: 'Start date cannot be after end date.'
                        });
                        return;
                    }
                }

                await this.asyncCritical(async () => {
                    await firstValueFrom(
                        CpmCTaskApiService.updateTask(event.task.uuid, {
                            [event.field]: DateUtils.toLocalISOString(newDate) ?? null
                        })
                    );
                    await this.onRefresh();
                });
            });
    }
    /**
     * Handle task selection from Gantt row click
     */
    public onGanttTaskSelected(task: TaskTreeViewExtension | null): void {
        if (task) {
            const node = TreeUtils.findTreeNodeByKey(this.treeItems, task.uuid);
            this.selectedTask = node ?? null;
        } else {
            this.selectedTask = null;
        }
        this.selectedTaskChange.next(this.selectedTask);
    }

    // ===============================================================
    // ===============================================================

    /**
     * On help button pressed
     */
    public override async onHelp(): Promise<void> {
        this._helpVisible = true;
    }
    /**
     * When the tree selection changed
     * @param selection New selection
     */
    public override onTreeSelectionChanged(
        selection: TreeNode<TaskTreeViewExtension> | TreeNode<TaskTreeViewExtension>[] | null
    ) {
        if (isArray(selection)) return;

        this.selectedTask = selection;
        this.selectedTaskChange.next(selection);
    }

    /**
     * When a task is selected from the details view
     * @param taskUuid The task to select
     */
    public onTaskSelectedFromDetails(taskUuid: string) {
        const node = TreeUtils.findTreeNodeByKey(this.treeItems, taskUuid);
        if (node) {
            this.onTreeSelectionChanged(node);

            const selected_node = TreeUtils.findTreeNodeByKey(this.treeItems, taskUuid);
            this._selection = selected_node;
        }
    }
    /**
     * When the context menu is shown on a tree node
     * @param contextNode Context menu node
     */
    public override onContextMenuSelect(row: TaskTreeViewExtension) {
        this.contextMenu = this.buildExtendedContextMenu(row);
    }
    /**
     * Arrow function wrapper to preserve 'this' context when passing to modal
     * This allows super.callDelete to be called with correct context
     */
    private readonly deleteItemMethod = (item: CpmCTaskApiModel, payload?: CpmCTaskDeleteApiSchema) => {
        return super.callDelete(item as unknown as TaskTreeViewExtension, false, payload);
    };
    /**
     * Override onDelete method to insert a warning modal before to delete modal
     * @param itemToDelete Tâche à supprimer
     * @param confirmed Confirmation déjà obtenue?
     */
    public override async onDelete(itemToDelete: TaskTreeViewExtension, payload?: CpmCTaskDeleteApiSchema) {
        const item = await firstValueFrom(CpmCTaskApiService.getTaskByUUID(itemToDelete.uuid));
        if (
            (item.dependent_relationships && item.dependent_relationships.length > 0) ||
            (item.dependency_relationships && item.dependency_relationships.length > 0)
        ) {
            // Pass the parentOnDelete arrow function to preserve 'this' context
            const config = new TaskDependsWarningModalConfig(item, this.items, this.deleteItemMethod);
            this.globalsService.dialogService
                .open(
                    TaskDependsWarningModalComponent,
                    TaskDependsWarningModalComponent.getDialogServiceParameters(config)
                )
                .onClose.subscribe((result: boolean) => {
                    if (result) {
                        super.refresh();
                    }
                });
            return;
        }

        super.onDelete(itemToDelete, payload);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Do a reorder on a task
     * @param task Task instance
     * @param mode Reorder mode
     */
    protected async reorderTask(
        task: TaskTreeViewExtension,
        mode: 'top' | 'up' | 'down' | 'bottom' | 'swap',
        swapWithUUID?: string
    ) {
        const node = TreeUtils.findTreeNodeByKey(this.treeItems, task.uuid);
        if (!node) return;

        await this.asyncCritical(async () => {
            const payload: CpmCTaskReorderApiSchema = {};

            switch (mode) {
                case 'top':
                    payload.move_to_top = true;
                    break;

                case 'up': {
                    const collection = node.parent?.children ?? this.treeItems;
                    const currentTaskIndex = collection.indexOf(node);
                    if (currentTaskIndex - 1 < 0) return;

                    payload.swap_task_uuid = collection[currentTaskIndex - 1].key;
                    break;
                }

                case 'down': {
                    const collection = node.parent?.children ?? this.treeItems;
                    const currentTaskIndex = collection.indexOf(node);
                    if (currentTaskIndex + 1 >= collection.length) return;

                    payload.swap_task_uuid = collection[currentTaskIndex + 1].key;
                    break;
                }

                case 'bottom':
                    payload.move_to_bottom = true;
                    break;

                case 'swap':
                    payload.swap_task_uuid = swapWithUUID;
                    break;
            }

            this.showLoading = true;
            await firstValueFrom(CpmCTaskApiService.reorderTask(task.uuid, payload));
        });

        await this.refresh();
        this.showLoading = false;
    }
    /**
     * Change task status
     * @param task Task instance
     * @param newStatus New status
     */
    protected async changeTaskStatus(task: TaskTreeViewExtension, newStatus: ECpmCTaskStatusApiEnum) {
        await this.asyncCritical(async () => {
            const updatedTask = await firstValueFrom(
                CpmCTaskApiService.updateTask(task.uuid, {
                    status: newStatus
                })
            );

            task.status = updatedTask.status;
            task.attributes1 = updatedTask.attributes1;
            void this.onRefresh();
        });
    }
    /**
     * Execute a task
     * @param task
     */
    protected async executeTask(task: TaskTreeViewExtension) {
        await this.asyncCritical(async () => {
            await firstValueFrom(CpmCTaskApiService.executeTask(task.uuid));

            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'success',
                detail: 'Started execution for task ' + task.name
            });
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected override async initColumns(): Promise<void> {
        this.treeCols = [
            {
                field: 'name',
                header: 'Tasks',
                searcheable: true,
                sortable: false,
                filterable: true,
                filterType: ETreeColFilterType.TEXT,
                nodeIconSide: 'right',
                tooltipField: 'name'
            },
            {
                field: 'status',
                header: 'Status',
                colWidth: '20rem',
                dataType: ETreeColDataType.DATA_DISPLAY_CELL_COMPONENT,
                dataCellDisplayComponent: TaskStatusDataCellDisplayComponent,
                searcheable: false,
                filterable: false,
                sortable: false,
                filterSelectOptions: TaskUtils.statusTags,
                filterType: ETreeColFilterType.SELECT
            },
            {
                field: 'due_date',
                header: 'Due',
                colWidth: '6rem',
                dataType: ETreeColDataType.DATE,
                customDateFormat: 'dd MMM',
                searcheable: true,
                sortable: false,
                filterable: true,
                filterType: ETreeColFilterType.DATE
            },
            {
                field: '_computed_deps_col',
                tooltipField: '_computed_deps_col_tooltip',
                header: 'Dep',
                colWidth: '6rem',
                searcheable: false,
                filterable: false,
                sortable: false,
                filterType: ETreeColFilterType.TEXT
            }
        ];
    }

    // ===============================================================
    // ===============================================================

    /**
     * Build an extended context menu including the base context menu items
     * @param row Current row
     * @returns Extended content menu items
     */
    protected buildExtendedContextMenu(row: TaskTreeViewExtension): MenuItem[] {
        const editAllowed = SdkBridge.isAuthorizedForRouteResourceUpdate(
            CpmCTaskApiService.endpointEdit(undefined, undefined, this.FAKE_UUID)
        );

        const node = TreeUtils.findTreeNodeByKey(this.treeItems, row.uuid);
        const collection = node?.parent?.children ?? this.treeItems;

        const menuItems: MenuItem[] = [];
        menuItems.push(...this.buildContextMenuMoveActions(node!, collection));

        if (row.task_type == ECpmCTaskTypeApiEnum.SMART && node) {
            menuItems.push({separator: true}, ...this.buildContextMenuExecuteInteractActions(node));
        } else if (row.task_type == ECpmCTaskTypeApiEnum.SIMPLE && node) {
            menuItems.push({separator: true}, ...this.buildContextMenuMilestoneActions(node, editAllowed));
        }

        menuItems.push(
            {separator: true},
            ...this.buildContextMenuStatusActions(node!, editAllowed),
            {separator: true},
            ...this.buildDefaultContextMenu(row)
        );

        return menuItems;
    }
    /**
     * Build move menu items
     * @param node Current node
     * @param collection Node current branch
     * @returns Menu items
     */
    private buildContextMenuMoveActions(
        node: TreeNode<TaskTreeViewExtension>,
        collection: TreeNode<TaskTreeViewExtension>[]
    ): MenuItem[] {
        const reorderAllowed = SdkBridge.isAuthorizedForRouteResourceUpdate(
            CpmCTaskApiService.endpointReorder(undefined, undefined, this.FAKE_UUID)
        );

        const isFirstSibling = node && collection.indexOf(node) == 0;
        const haveSiblings = node && collection.length > 0;
        const isLastSibling = node && collection.indexOf(node) == collection.length - 1;

        return [
            {
                label: 'Move to top',
                icon: 'pi pi-sort-amount-up-alt',
                disabled: !(reorderAllowed && !isFirstSibling),
                command: () => this.reorderTask(node.data!, 'top')
            },
            {
                label: 'Move up',
                icon: 'pi pi-arrow-up',
                disabled: !(reorderAllowed && !isFirstSibling && haveSiblings),
                command: () => this.reorderTask(node.data!, 'up')
            },
            {
                label: 'Move down ',
                icon: 'pi pi-arrow-down',
                disabled: !(reorderAllowed && !isLastSibling && haveSiblings),
                command: () => this.reorderTask(node.data!, 'down')
            },
            {
                label: 'Move to bottom',
                icon: 'pi pi-sort-amount-down-alt',
                disabled: !(reorderAllowed && !isLastSibling),
                command: () => this.reorderTask(node.data!, 'bottom')
            },
            {
                label: 'Swap with',
                icon: 'pi pi-arrow-right-arrow-left',
                disabled: !(reorderAllowed && haveSiblings),
                items: collection.map((x) => ({
                    label: x.label,
                    icon: 'pi pi-pen-to-square',
                    disabled: !(reorderAllowed && haveSiblings && x != node),
                    command: () => this.reorderTask(node.data!, 'swap', x.key!)
                }))
            }
        ];
    }
    /**
     * Build execute/interact menu items
     * @param node Current node
     * @returns Menu items
     */
    private buildContextMenuExecuteInteractActions(node: TreeNode<TaskTreeViewExtension>): MenuItem[] {
        const requiresManualInput =
            (node.data!.attributes1 & ECpmCTaskAttributes1ApiEnum.SMART_REQUIRES_MANUAL_INPUT) !== 0;
        const executeLabel = requiresManualInput ? 'Validate' : 'Execute';
        const executeEnabled =
            SdkBridge.isAuthorizedForRouteResourceCreate(
                CpmCTaskApiService.endpointExecute(undefined, undefined, this.FAKE_UUID)
            ) && TaskUtils.canExecuteTask(node.data);
        const historyEnabled = SdkBridge.isAuthorizedForRouteResourceRead(
            CpmCTaskExecutionApiService.endpointList(undefined, undefined, this.FAKE_UUID)
        );

        return [
            {
                label: executeLabel,
                icon: 'pi pi-play',
                disabled: !executeEnabled,
                command: () => this.executeTask(node.data!)
            },
            {
                label: 'Execution History',
                icon: 'pi pi-history',
                disabled: !historyEnabled,
                command: () => {
                    this.globalsService.dialogService.open(
                        TaskExecutionsModalComponent,
                        TaskExecutionsModalComponent.getDialogServiceParameters({
                            taskUUID: node.key!,
                            taskName: node.data?.name
                        })
                    );
                }
            }
        ];
    }
    /**
     * Build status menu items
     * @param node Current node
     * @param editAllowed Is edit allowed
     * @returns Menu items
     */
    private buildContextMenuStatusActions(node: TreeNode<TaskTreeViewExtension>, editAllowed: boolean): MenuItem[] {
        const enabled = editAllowed && TaskUtils.canChangeTaskStatus(node.data!);

        return [
            {
                label: 'Status',
                icon: 'pi pi-circle',
                disabled: !enabled,
                items: TaskUtils.allowedStatusChanges.map((x) => ({
                    label: x.label,
                    disabled: !(
                        enabled && TaskUtils.canChangeTaskStatusTo(node.data!, x.value as ECpmCTaskStatusApiEnum)
                    ),
                    command: () => this.changeTaskStatus(node.data!, x.value as ECpmCTaskStatusApiEnum)
                }))
            }
        ];
    }

    /**
     * Build milestone toggle menu item
     * @param node Current node
     * @param editAllowed Is edit allowed
     * @returns Menu items
     */
    private buildContextMenuMilestoneActions(node: TreeNode<TaskTreeViewExtension>, editAllowed: boolean): MenuItem[] {
        const isMilestone = TaskUtils.isMilestone(node.data);

        return [
            {
                label: isMilestone ? 'Remove milestone' : 'Set as milestone',
                icon: 'pi pi-flag',
                disabled: !editAllowed,
                command: () => this.toggleMilestone(node.data!)
            }
        ];
    }

    /**
     * Toggle milestone flag on a task
     * @param task Task to toggle milestone
     */
    protected async toggleMilestone(task: TaskTreeViewExtension) {
        await this.asyncCritical(async () => {
            const payload: CpmCTaskUpdateApiSchema = {
                is_milestone: !TaskUtils.isMilestone(task)
            };

            await firstValueFrom(CpmCTaskApiService.updateTask(task.uuid, payload));
            this.refresh();
        });
    }
}
